import React, { useEffect, createElement } from 'react';
interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: string;
  robots?: string;
  jsonLd?: object | object[];
}
export function SEO({
  title,
  description,
  keywords,
  canonicalPath,
  ogImage = 'https://4all.ai/og-image.jpg',
  ogType = 'website',
  robots = 'index, follow',
  jsonLd
}: SEOProps) {
  useEffect(() => {
    // Set document title
    document.title = title.includes('4ALL AI') ? title : `${title} | 4ALL AI`;
    // Helper to create or update meta tags
    const setMetaTag = (attrName: string, attrValue: string, content: string) => {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        element.setAttribute('data-seo', 'true');
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };
    // Standard meta tags
    setMetaTag('name', 'description', description);
    setMetaTag('name', 'robots', robots);
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }
    // Open Graph
    setMetaTag('property', 'og:title', title);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:site_name', '4ALL AI');
    if (canonicalPath) {
      setMetaTag('property', 'og:url', `https://4all.ai${canonicalPath}`);
    }
    // Twitter
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', title);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImage);
    // Canonical Link
    if (canonicalPath) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('data-seo', 'true');
        document.head.appendChild(link);
      }
      link.setAttribute('href', `https://4all.ai${canonicalPath}`);
    }
    // JSON-LD
    if (jsonLd) {
      const script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-seo', 'true');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
    // Cleanup on unmount
    return () => {
      const seoElements = document.querySelectorAll('[data-seo="true"]');
      seoElements.forEach(el => el.remove());
    };
  }, [title, description, keywords, canonicalPath, ogImage, ogType, robots, jsonLd]);
  return null;
}